import React, { useState, useEffect } from 'react';
import { TrendingUp, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PerformanceTrends, SkillRadarChart, TestHistoryTimeline } from '../../components/Analytics';
import { getUserAnalytics, getPerformanceComparison } from '../../services/analyticsService';

const Analytics: React.FC = () => {
  const { user } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [comparison, setComparison] = useState<{
    userAverage: number;
    platformAverage: number;
    percentile: number;
    betterThanPercent: number;
  } | null>(null);

  useEffect(() => {
    loadComparison();
  }, [user]);

  const loadComparison = async () => {
    if (user?.id) {
      const data = await getPerformanceComparison(user.id);
      setComparison(data);
    }
  };

  const handleRefresh = async () => {
    if (!user?.id) return;

    setRefreshing(true);
    // Force refresh analytics
    await getUserAnalytics(user.id, true);
    await loadComparison();
    setRefreshing(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center space-x-3">
              <TrendingUp className="w-8 h-8" />
              <span>Performance Analytics</span>
            </h1>
            <p className="text-blue-100 mt-2">Deep insights into your test performance and skill development</p>
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Platform Comparison Card */}
      {comparison && (
        <div className="bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold mb-4">🏆 Your Ranking</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-sm opacity-90">Your Average</p>
              <p className="text-3xl font-bold mt-1">{comparison.userAverage}%</p>
            </div>

            <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-sm opacity-90">Platform Average</p>
              <p className="text-3xl font-bold mt-1">{comparison.platformAverage}%</p>
            </div>

            <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-sm opacity-90">Percentile</p>
              <p className="text-3xl font-bold mt-1">{comparison.percentile}th</p>
            </div>

            <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-sm opacity-90">Better Than</p>
              <p className="text-3xl font-bold mt-1">{comparison.betterThanPercent}%</p>
            </div>
          </div>

          {comparison.userAverage > comparison.platformAverage && (
            <div className="mt-4 p-3 bg-green-500/30 rounded-lg backdrop-blur-sm">
              <p className="text-sm font-medium">
                🎉 Amazing! You're performing {Math.round(comparison.userAverage - comparison.platformAverage)}% above the platform average!
              </p>
            </div>
          )}
        </div>
      )}

      {/* Performance Trends */}
      <PerformanceTrends userId={user?.id || ''} compact={false} />

      {/* Skills and History Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Skill Radar Chart */}
        <SkillRadarChart userId={user?.id || ''} compact={false} />

        {/* Test History Timeline */}
        <TestHistoryTimeline userId={user?.id || ''} limit={5} compact={false} />
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-6">
        <h3 className="font-semibold text-cyan-900 mb-3">📊 How to Improve Your Analytics</h3>
        <ul className="space-y-2 text-sm text-cyan-800">
          <li>• Take tests consistently to build reliable trend data</li>
          <li>• Focus on improving skills where you scored below 70%</li>
          <li>• Review your test history to identify patterns in performance</li>
          <li>• Complete both MCQ and AI interview portions for comprehensive assessments</li>
          <li>• Track your percentile ranking and aim for top 25%</li>
        </ul>
      </div>
    </div>
  );
};

export default Analytics;
